import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfColor,
  PdfFont,
  PdfPen,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { OptsImage, ImageTuple } from '../optsImage';
import { IItemCount } from '@educraft/base';
import { GUtil } from '../../pdf/gutil';
import { drawDitherLogo } from '../../pdf/ditherLogo';
import '../../pdf/draw/primitives';
import '../../pdf/draw/image';
import '../../pdf/draw/cut';
import '../../pdf/draw/string';
import '../../pdf/draw/helpers';
import '../../pdf/draw/shapes';

/**
 * Monopoly - Board game layout
 *
 * 4 landscape pages:
 * - Page 0: Game board (24 perimeter squares) + center DitherLogo "MONOPOLY"
 * - Page 1: DitherLogo card backs (3×4 grid)
 * - Pages 2-3: Action/chance cards with text (3×4 grid)
 *
 * Board has: Start, Jail, Parking, GoToJail corners + property/action squares
 * Properties show colored header + image + label + price
 *
 * Migrated from C# EduFrame.Img10.Generators.Monopoly
 */

const enum EBoxType {
  Property,
  Start,
  Action,
  Jail,
  Parking,
  GoToJail,
}

interface Board {
  boxType: EBoxType;
  color: PdfColor;
  image: any; // PdfImage
  text: string;
  price: number | null;
  angle: number;
}

interface Card {
  texts: string[];
  board: Board | null;
}

export class Monopoly implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 16;
  }
  countMin(): number {
    return 8;
  }
  countMax(): number {
    return 16;
  }

  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const pages = [pdf.addPage(true), pdf.addPage(true), pdf.addPage(true), pdf.addPage(true)];

    const margin = PdfUnit.FromMillimeters(5);
    const cX = 3,
      cY = 4;
    const maxSize = PdfSize.A4H.height - 2 * margin;
    const sz1 = PdfUnit.FromMillimeters(35);
    const sz2 = (maxSize - 2 * sz1) / 5;
    const colorSize = PdfUnit.FromMillimeters(5);
    const labelSize = PdfUnit.FromMillimeters(10);
    const boardRect = new PdfRect((PdfSize.A4H.width - maxSize) / 2, margin, maxSize, maxSize);
    const cardW = PdfSize.A4H.width / cX;
    const cardH = PdfSize.A4H.height / cY;
    const cardRect = new PdfRect(
      (PdfSize.A4H.width - cardW) / 2,
      (PdfSize.A4H.height - cardH) / 2,
      cardW,
      cardH
    );

    const colors = [
      PdfColor.fromRgb(139, 69, 19), // Brown
      PdfColor.fromRgb(0, 191, 255), // DeepSkyBlue
      PdfColor.fromRgb(238, 130, 238), // Violet
      PdfColor.fromRgb(255, 165, 0), // Orange
      PdfColor.fromRgb(255, 0, 0), // Red
      PdfColor.fromRgb(255, 255, 0), // Yellow
      PdfColor.fromRgb(0, 128, 0), // Green
      PdfColor.fromRgb(0, 0, 255), // Blue
    ];

    const seed = opts.data.random.next(0, 2147483647);
    const rng = opts.data.random;
    const {lang} = opts.data;

    const startLabel = lang.Monopoly_Start ?? 'START';
    const chanceLabel = lang.Monopoly_Chance ?? 'ŠANCE';
    const jailLabel = lang.Monopoly_Jail ?? 'VĚZENÍ';
    const parkingLabel = lang.Monopoly_Parking ?? 'PARKOVÁNÍ';
    const goToJailLabel = lang.Monopoly_GoToJail ?? 'Jdi do VĚZENÍ';
    const justVisitingLabel = lang.Monopoly_JustVisiting ?? 'Jen na';
    const visitingLabel = lang.Monopoly_Visiting ?? 'Visiting';

    // Build board rectangles (24 positions around perimeter)
    const rects: PdfRect[] = [];

    // Bottom row (right to left)
    for (let i = 0; i < 6; i++) {
      rects.push(
        i === 0
          ? new PdfRect(boardRect.right - sz1, boardRect.bottom - sz1, sz1, sz1)
          : new PdfRect(boardRect.right - sz1 - i * sz2, boardRect.bottom - sz1, sz2, sz1)
      );
    }
    // Left column (bottom to top)
    for (let i = 0; i < 6; i++) {
      rects.push(
        i === 0
          ? new PdfRect(boardRect.x, boardRect.bottom - sz1, sz1, sz1)
          : new PdfRect(boardRect.x, boardRect.bottom - sz1 - i * sz2, sz1, sz2)
      );
    }
    // Top row (left to right)
    for (let i = 0; i < 6; i++) {
      rects.push(
        i === 0
          ? new PdfRect(boardRect.x, boardRect.y, sz1, sz1)
          : new PdfRect(boardRect.x + sz1 + (i - 1) * sz2, boardRect.y, sz2, sz1)
      );
    }
    // Right column (top to bottom)
    for (let i = 0; i < 6; i++) {
      rects.push(
        i === 0
          ? new PdfRect(boardRect.right - sz1, boardRect.y, sz1, sz1)
          : new PdfRect(boardRect.right - sz1, boardRect.y + sz1 + (i - 1) * sz2, sz1, sz2)
      );
    }

    // Build board data
    const board: Board[] = [];
    const pb = (boxType: EBoxType, angle = 0): Board => ({
      boxType,
      color: PdfColor.fromRgb(0, 0, 0),
      image: null,
      text: '',
      price: null,
      angle,
    });
    const pp = (
      colorIdx: number,
      text: string,
      price: number,
      angle: number,
      image: any
    ): Board => ({
      boxType: EBoxType.Property,
      color: colors[colorIdx],
      image,
      text,
      price,
      angle,
    });

    const getPropertyData = (slotIdx: number): { label: string; image: any } => {
      const itemCount = opts.list.length;

      if (itemCount === 16) {
        return { label: opts.list[slotIdx].label, image: opts.list[slotIdx].image };
      }

      if (itemCount === 8) {
        const imageIdx = Math.floor(slotIdx / 2);
        const item = opts.list[imageIdx];
        const suffix = slotIdx % 2 === 1 ? ' 2' : ' 1';
        return { label: item.label + suffix, image: item.image };
      }

      const pairedCount = 16 - itemCount;
      const isPaired = slotIdx < pairedCount;

      if (!isPaired) {
        return {
          label: opts.list[slotIdx % itemCount].label,
          image: opts.list[slotIdx % itemCount].image,
        };
      }

      const imageIdx = Math.floor(slotIdx / 2);
      const item = opts.list[imageIdx % itemCount];
      const suffix = slotIdx % 2 === 1 ? ' 2' : ' 1';
      return { label: item.label + suffix, image: item.image };
    };

    const propertyAngles = [0, 0, 0, 0, 270, 270, 270, 270, 180, 180, 180, 180, 90, 90, 90, 90];
    const propertyPrices = [1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 5, 5];

    board.push(pb(EBoxType.Start));
    let propertyIdx = 0;
    for (let pos = 1; pos < 24; pos++) {
      if (pos === 6) {
        board.push(pb(EBoxType.Jail));
      } else if (pos === 12) {
        board.push(pb(EBoxType.Parking));
      } else if (pos === 18) {
        board.push(pb(EBoxType.GoToJail));
      } else if ([3, 9, 15, 21].includes(pos)) {
        const actionAngle = [0, 270, 180, 90][Math.floor((pos % 24) / 6)];
        board.push(pb(EBoxType.Action, actionAngle));
      } else {
        const colorIdx = propertyIdx % 8;
        const propData = getPropertyData(propertyIdx);
        board.push(
          pp(
            colorIdx,
            propData.label,
            propertyPrices[propertyIdx],
            propertyAngles[propertyIdx],
            propData.image
          )
        );
        propertyIdx++;
      }
    }

    const properties = board.filter((b) => b.boxType === EBoxType.Property);

    const tl = (key: string, fallback: string, ...args: (string | number)[]): string => {
      let text = lang[key] ?? fallback;
      for (let i = 0; i < args.length; i++) {
        text = text.replace(`{${i}}`, String(args[i]));
      }
      return text;
    };

    const templates: Card[] = [];
    const t = (texts: string[], b: Board | null = null): Card => ({ texts, board: b });

    templates.push(
      t([
        tl('Monopoly_Cards_LeaveJailFree', 'Leave jail without paying.'),
        '',
        tl('Monopoly_Cards_KeepCard', 'Keep this card until you need it.'),
      ])
    );
    templates.push(
      t([
        tl('Monopoly_Cards_GoodLawyer', 'Good lawyer.'),
        '',
        tl('Monopoly_Cards_KeepCard', 'Keep this card, it will get you out of jail once.'),
      ])
    );
    templates.push(
      t([
        tl('Monopoly_Cards_TasksDone', 'You have completed all tasks.'),
        '',
        tl('Monopoly_Cards_FromBank', 'Collect {0} M from the bank.', 1 + rng.next(0, 5)),
      ])
    );
    templates.push(
      t([tl('Monopoly_Cards_MoveForward', 'Move forward {0} spaces.', 2 + rng.next(0, 3))])
    );
    templates.push(
      t([
        tl('Monopoly_Cards_MoveForward', 'Move forward {0} spaces.', 2 + rng.next(0, 3)),
        tl('Monopoly_Cards_Or', 'or'),
        tl('Monopoly_Cards_TakeAnotherChance', 'take another {0} card.', chanceLabel),
      ])
    );
    templates.push(
      t([
        tl('Monopoly_Cards_Birthday', "It's your birthday!"),
        '',
        tl('Monopoly_Cards_BirthdayEach', 'Everyone gives you {0} M.', 1 + rng.next(0, 3)),
      ])
    );
    templates.push(
      t([
        tl('Monopoly_Cards_NameDay', "It's your name day!"),
        '',
        tl('Monopoly_Cards_NameDayEach', 'Everyone gives you 1 M.', 1),
      ])
    );
    templates.push(
      t([
        tl('Monopoly_Cards_GoToStart', 'Go to {0}.', startLabel),
        '',
        tl('Monopoly_Cards_GoToStartReward', 'Collect 2 M from the bank.'),
      ])
    );
    templates.push(
      t([
        tl('Monopoly_Cards_Sweets', 'You ate too many sweets.'),
        '',
        tl('Monopoly_Cards_SweetsFine', 'Pay the bank {0} M.', 1 + rng.next(0, 3)),
      ])
    );
    templates.push(
      t([
        tl('Monopoly_Cards_StockMarket', 'Stock market rise!'),
        '',
        tl(
          'Monopoly_Cards_StockMarketReward',
          'Get {0} M for each property you own.',
          1 + rng.next(0, 2)
        ),
      ])
    );
    templates.push(
      t([
        tl('Monopoly_Cards_Lottery', 'Lottery win!'),
        '',
        tl('Monopoly_Cards_LotteryReward', 'The bank pays you {0} M.', 5 + rng.next(0, 6)),
      ])
    );
    templates.push(
      t([
        tl('Monopoly_Cards_Bingo', 'Bingo win!'),
        '',
        tl('Monopoly_Cards_BingoReward', 'The bank pays you {0} M.', 1 + rng.next(0, 5)),
      ])
    );
    templates.push(
      t([
        tl('Monopoly_Cards_FoundMoney', 'Found money on the street.'),
        '',
        tl('Monopoly_Cards_FoundMoneyReward', 'Receive {0} M from the bank.', 1 + rng.next(0, 3)),
      ])
    );
    templates.push(
      t([
        tl('Monopoly_Cards_LuckyChild', 'Lucky child!'),
        '',
        tl('Monopoly_Cards_LuckyChildAction', 'Go to any space.'),
      ])
    );
    templates.push(
      t([
        tl('Monopoly_Cards_FastShoes', 'Fast shoes!'),
        '',
        tl('Monopoly_Cards_FastShoesAction', 'Roll again.'),
      ])
    );
    templates.push(
      t([
        tl('Monopoly_Cards_StockBook', 'Stock book.'),
        '',
        tl(
          'Monopoly_Cards_StockBookKeep',
          'Keep this card until you pass START. When you pass, receive {0} M total.',
          3 + rng.next(0, 4)
        ),
      ])
    );
    templates.push(
      t([
        tl('Monopoly_Cards_Sportka', 'Sportka lottery.'),
        '',
        tl(
          'Monopoly_Cards_SportkaReward',
          'Receive from the bank as much as you roll on the dice.'
        ),
      ])
    );
    templates.push(
      t([
        tl(
          'Monopoly_Cards_KeepCard2',
          'Keep this card. Once you can go forward one space anytime.'
        ),
      ])
    );
    templates.push(t([tl('Monopoly_Cards_BuyProperty', 'Go to any property you own.')]));
    templates.push(
      t([
        tl('Monopoly_Cards_RealEstateAgent', 'Real estate agent'),
        '',
        tl(
          'Monopoly_Cards_BuyAnyPropertyFull',
          'You may buy any property, even one owned by someone else.'
        ),
      ])
    );
    templates.push(t([tl('Monopoly_Cards_BuyAnyProperty', 'You may buy any unowned property.')]));
    templates.push(
      t([
        tl(
          'Monopoly_Cards_CollectFromBank',
          'Collect from the bank as much as your nearest property costs.'
        ),
      ])
    );
    templates.push(
      t([tl('Monopoly_Cards_SellToBank', 'Sell one of your properties to the bank.')])
    );
    templates.push(
      t([tl('Monopoly_Cards_BuyFromAnyone', 'From any player you may buy any property.')])
    );
    templates.push(
      t([
        tl('Monopoly_Cards_SellToBankDouble', 'Sell one property to the bank.'),
        '',
        tl('Monopoly_Cards_SellToBankDouble', 'Receive double the price.'),
      ])
    );
    templates.push(
      t([
        tl(
          'Monopoly_Cards_ChoosePlayer',
          'Choose a player who must give you one property of your choice.'
        ),
      ])
    );
    templates.push(
      t([
        tl('Monopoly_Cards_Bankruptcy', 'Personal bankruptcy.'),
        '',
        tl('Monopoly_Cards_BankruptcyAction', 'Give one of your properties to the bank.'),
      ])
    );
    templates.push(t([tl('Monopoly_Cards_GoForward', 'Go forward to the corner.')]));
    templates.push(
      t([tl('Monopoly_Cards_GoToNextChance', 'Go forward to the next {0} space.', chanceLabel)])
    );
    templates.push(t([tl('Monopoly_Cards_GoToNextProperty', 'Go forward to the next property.')]));
    templates.push(
      t([
        tl(
          'Monopoly_Cards_GoToYourProperty',
          'Go to your own property, receive rent from the bank.'
        ),
      ])
    );
    templates.push(
      t([
        tl(
          'Monopoly_Cards_CompleteSet',
          'For each complete set of properties receive {0} M.',
          3 + rng.next(0, 3)
        ),
      ])
    );

    // Property-specific cards
    const randProp = (exclude?: Board) => {
      const filtered = properties.filter(
        (p) => p !== (exclude || properties[properties.length - 1])
      );
      return filtered[rng.next(0, filtered.length)];
    };

    const p1 = randProp();
    templates.push(t([tl('Monopoly_Cards_GoToProperty', 'Go to {0}.', p1.text.toUpperCase())], p1));
    templates.push(
      t(
        [
          tl(
            'Monopoly_Cards_GoToProperty',
            'Go to {0}.',
            properties[properties.length - 1].text.toUpperCase()
          ),
        ],
        properties[properties.length - 1]
      )
    );

    const p2 = randProp();
    templates.push(
      t(
        [
          tl('Monopoly_Cards_GoToProperty', 'Go to {0}.', p2.text.toUpperCase()),
          '',
          tl(
            'Monopoly_Cards_GoToPropertyFree',
            'Go to {0}. If unowned, you get it free, otherwise pay rent.',
            p2.text.toUpperCase()
          ),
        ],
        p2
      )
    );

    const p3 = randProp();
    templates.push(
      t(
        [
          tl(
            'Monopoly_Cards_GoToPropertyPassStart',
            'Go to {0}. If you pass START, receive {1} M.',
            p3.text.toUpperCase(),
            1 + rng.next(0, 5)
          ),
        ],
        p3
      )
    );

    // AI cards
    templates.push(
      t([
        tl(
          'Monopoly_Cards_SwapProperty',
          'Swap one property with any player. If they refuse, receive {0} M.',
          5 + rng.next(0, 10)
        ),
      ])
    );
    templates.push(
      t([
        tl(
          'Monopoly_Cards_PlayerRightPays',
          'Player to your right pays {0} M for your refreshments.',
          1 + rng.next(0, 3)
        ),
      ])
    );
    templates.push(
      t([
        tl(
          'Monopoly_Cards_Fountain',
          'You bathed in the fountain! Pay {0} M fine or give one property.',
          2 + rng.next(0, 3)
        ),
      ])
    );
    templates.push(t([tl('Monopoly_Cards_SwapPositions', 'Swap positions with any player.')]));
    templates.push(
      t([tl('Monopoly_Cards_Tunnel', 'Secret shortcut! Go through the tunnel over 3 spaces.')])
    );
    templates.push(
      t([tl('Monopoly_Cards_Gambling', 'Gamble! Roll dice: 1-3: Lose 4 M, 4-6: Win 6 M')])
    );
    templates.push(
      t([
        tl(
          'Monopoly_Cards_Treasure',
          'Treasure on the island! Stay another turn and receive {0} M.',
          5 + rng.next(0, 6)
        ),
      ])
    );
    templates.push(
      t([
        tl(
          'Monopoly_Cards_Detour',
          "Quick detour! Next time you don't have to pay rent. Keep this card."
        ),
      ])
    );
    templates.push(
      t([tl('Monopoly_Cards_WheelBroke', 'Wheel broke! Random player pays 3 M for repairs.')])
    );
    templates.push(t([tl('Monopoly_Cards_Parkour', 'Parkour trick! Skip 2 spaces and take 1 M.')]));
    templates.push(
      t([tl('Monopoly_Cards_Race', 'Race wheel! Roll 2× and choose the better roll.')])
    );
    templates.push(
      t([
        tl(
          'Monopoly_Cards_Vacation',
          'Hurrah, vacation! Pay the bank {0} M, then go to START.',
          2 + rng.next(0, 2)
        ),
      ])
    );
    templates.push(
      t([
        tl(
          'Monopoly_Cards_Choose',
          'Choose: either move {0} spaces, or take {0} M from the bank.',
          2 + rng.next(0, 3)
        ),
      ])
    );
    templates.push(
      t([
        tl(
          'Monopoly_Cards_Audit',
          'Random audit! If you have more than 5 M, pay 2 M. If less, receive 1 M.'
        ),
      ])
    );

    // Shuffle and take needed number of cards
    const shuffledTemplates = [...templates];
    for (let i = shuffledTemplates.length - 1; i > 0; i--) {
      const j = rng.next(0, i + 1);
      [shuffledTemplates[i], shuffledTemplates[j]] = [shuffledTemplates[j], shuffledTemplates[i]];
    }
    const totalCardSlots = (pages.length - 2) * cX * cY;
    const cards = shuffledTemplates.slice(0, totalCardSlots);

    const f = pages[0].adjustFont(
      sz2,
      board.map((b) => b.text),
      GUtil.baseFont
    );
    const fA = new PdfFont(PdfFont.FONT_NAME, 64);
    const fSmall = new PdfFont(PdfFont.FONT_NAME, GUtil.baseFont.size / 2);

    // Draw board (page 0)
    for (let index = 0; index < rects.length; index++) {
      const r = rects[index];
      const b = board[index];

      if (b.boxType === EBoxType.Start) {
        const r1 = new PdfRect(r.x, r.y, r.width, r.height / 2);
        const r2 = new PdfRect(r.x, r.y + r.height / 2, r.width, r.height / 2);
        pages[0].drawString(
          startLabel,
          GUtil.baseFont,
          PdfBrush.Black,
          null,
          r1,
          ETextAlign.Center
        );
        pages[0].drawArrowLeft(r2, new PdfBrush(PdfColor.fromRgb(0, 0, 139)));
        pages[0].drawRectangle(GUtil.basePen, null, r);
      } else if (b.boxType === EBoxType.Property) {
        const rr = new PdfRect(r.x + r.width / 2 - sz2 / 2, r.y + r.height / 2 - sz1 / 2, sz2, sz1);
        const r1 = new PdfRect(rr.x, rr.y, sz2, colorSize);
        const r3 = new PdfRect(r1.x, rr.y + rr.height - labelSize, sz2, labelSize);
        const r2 = new PdfRect(r1.x, r1.y + r1.height, sz2, r3.y - r1.y - r1.height);

        const ff: PdfFont = { font: PdfFont.FONT_NAME, size: 12 };

        pages[0].save();
        pages[0].rotateAtTransform(b.angle, new PdfPoint(r.x + r.width / 2, r.y + r.height / 2));

        pages[0].drawRectangle(null, new PdfBrush(b.color), r1);
        if (b.price != null) {
          const ppSize = r1.height;
          const ppRect = new PdfRect(r1.x + (r1.width - ppSize) / 2, r1.y, ppSize, ppSize);
          pages[0].drawEllipse(null, new PdfBrush(PdfColor.fromRgb(255, 255, 255)), ppRect);
          pages[0].drawString(`${b.price}`, ff, PdfBrush.Black, null, r1, ETextAlign.Center);
        }
        pages[0].drawRectangle(GUtil.basePen, null, r1);
        pages[0].drawBackground(r2);
        pages[0].drawImageCenter(b.image, r2);
        pages[0].drawString(b.text, f, PdfBrush.Black, null, r3, ETextAlign.Center);
        pages[0].drawRectangle(GUtil.basePen, null, r3);
        pages[0].drawRectangle(GUtil.basePen, null, rr);
        pages[0].restore();
      } else if (b.boxType === EBoxType.Action) {
        const rr = new PdfRect(r.x + r.width / 2 - sz2 / 2, r.y + r.height / 2 - sz1 / 2, sz2, sz1);
        const r1 = new PdfRect(rr.x, rr.y, sz2, rr.height - labelSize);
        const r2 = new PdfRect(r1.x, rr.y + rr.height - labelSize, sz2, labelSize);

        pages[0].save();
        pages[0].rotateAtTransform(b.angle, new PdfPoint(r.x + r.width / 2, r.y + r.height / 2));
        pages[0].drawString(
          '?',
          fA,
          new PdfBrush(PdfColor.fromRgb(0, 0, 139)),
          null,
          r1,
          ETextAlign.Center
        );
        pages[0].drawString(chanceLabel, f, PdfBrush.Black, null, r2, ETextAlign.Center);
        pages[0].drawRectangle(GUtil.basePen, null, r2);
        pages[0].drawRectangle(GUtil.basePen, null, rr);
        pages[0].restore();
      } else if (b.boxType === EBoxType.Jail) {
        const rr = new PdfRect(r.x + labelSize, r.y, r.width - labelSize, r.height - labelSize);
        const r1 = new PdfRect(r.x, r.y, labelSize, r.height - labelSize);
        const r2 = new PdfRect(r.x + labelSize, rr.y + rr.height, r.width - labelSize, labelSize);

        pages[0].save();
        pages[0].rotateAtTransform(90, new PdfPoint(r1.x + r1.width / 2, r1.y + r1.height / 2));
        pages[0].drawString(justVisitingLabel, fSmall, PdfBrush.Black, null, r1, ETextAlign.Center);
        pages[0].restore();
        pages[0].drawString(visitingLabel, fSmall, PdfBrush.Black, null, r2, ETextAlign.Center);

        pages[0].drawRectangle(null, new PdfBrush(PdfColor.fromRgb(255, 165, 0)), rr);
        pages[0].save();
        pages[0].rotateAtTransform(45, new PdfPoint(rr.x + rr.width / 2, rr.y + rr.height / 2));
        pages[0].drawString(jailLabel, fSmall, PdfBrush.Black, null, rr, ETextAlign.Center);
        pages[0].restore();

        pages[0].drawRectangle(GUtil.basePen, null, rr);
        pages[0].drawRectangle(GUtil.basePen, null, r);
      } else if (b.boxType === EBoxType.Parking) {
        pages[0].save();
        pages[0].rotateAtTransform(135, new PdfPoint(r.x + r.width / 2, r.y + r.height / 2));
        pages[0].drawString(parkingLabel, fSmall, PdfBrush.Black, null, r, ETextAlign.Center);
        pages[0].restore();
        pages[0].drawRectangle(GUtil.basePen, null, r);
      } else if (b.boxType === EBoxType.GoToJail) {
        pages[0].save();
        pages[0].rotateAtTransform(225, new PdfPoint(r.x + r.width / 2, r.y + r.height / 2));
        pages[0].drawString(goToJailLabel, fSmall, PdfBrush.Black, null, r, ETextAlign.Center);
        pages[0].restore();
        pages[0].drawRectangle(GUtil.basePen, null, r);
      } else {
        pages[0].drawString(`${index}`, GUtil.baseFont, PdfBrush.Black, null, r, ETextAlign.Center);
        pages[0].drawRectangle(GUtil.basePen, null, r);
      }
    }

    // Center DitherLogo on board
    await drawDitherLogo(pages[0], 'MONOPOLY', PdfColor.fromRgb(0, 0, 139), cardRect, seed);
    pages[0].drawRectangle(GUtil.basePen, null, cardRect);

    // Pages 1+: Cards
    const darkBlueBrush = PdfColor.fromRgb(0, 0, 139);
    for (let page = 1; page < pages.length; page++) {
      for (let y = 0; y < cY; y++) {
        for (let x = 0; x < cX; x++) {
          const r = new PdfRect(x * cardW, y * cardH, cardW, cardH);

          if (page === 1) {
            await drawDitherLogo(pages[page], 'MONOPOLY', darkBlueBrush, r, seed);
          } else {
            const cardIndex = (page - 2) * cX * cY + y * cX + x;
            if (cardIndex >= cards.length) continue;

            const card = cards[cardIndex];
            const rr = new PdfRect(
              r.x + margin,
              r.y + margin,
              r.width - 2 * margin,
              r.height - 2 * margin
            );

            if (card.board == null) {
              pages[page].drawStringsMulti(
                card.texts,
                GUtil.baseFont,
                PdfBrush.Black,
                null,
                rr,
                ETextAlign.Center
              );
            } else {
              const r1 = new PdfRect(rr.x, rr.y, rr.width * 0.6, rr.height);
              const r2 = new PdfRect(r1.x + r1.width, rr.y, rr.width - r1.width, r1.height);

              pages[page].drawBackground(r2);
              pages[page].drawImageCenter(card.board.image, r2);
              pages[page].drawStringsMulti(
                card.texts,
                GUtil.baseFont,
                PdfBrush.Black,
                null,
                r1,
                ETextAlign.Center
              );
            }

            pages[page].drawRectangle(GUtil.cutPen, null, r);
          }
        }
      }
    }
  }
}
